import styled, { css, keyframes } from "styled-components";

import { colors } from "@/styles/design-system";

/* ── Animations ── */

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const slideDown = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(24px) scale(0.97); }
`;

const btnShine = keyframes`
  0%   { left: -100%; }
  100% { left: 150%; }
`;

const cardSweep = keyframes`
  0%   { transform: translateX(-120%) skewX(-12deg); }
  100% { transform: translateX(280%) skewX(-12deg); }
`;

/* ── Overlay ── */

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

/* ── Modal container ── */

export const ModalContainer = styled.div<{ $closing?: boolean }>`
  /* Static glass snapshot (2026-05-13) — see CartModal/styles.ts for context. */
  background:
    radial-gradient(ellipse 45% 40% at 42% -3%, rgba(176, 144, 105, 0.9) 0%, rgba(160, 128, 90, 0.5) 35%, transparent 75%),
    radial-gradient(ellipse 55% 50% at 58% 3%, rgba(170, 140, 100, 0.75) 0%, rgba(140, 110, 78, 0.35) 45%, transparent 85%),
    radial-gradient(ellipse 70% 35% at 50% 8%, rgba(150, 118, 82, 0.4) 0%, transparent 70%),
    linear-gradient(180deg, rgb(153, 112, 75) 0%, rgb(99, 74, 54) 50%, rgb(111, 83, 60) 100%);
  border: none;
  border-radius: 24px;
  max-width: 1000px;
  width: 100%;
  position: relative;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7), 0 20px 60px rgba(0, 0, 0, 0.6);
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${({ $closing }) => ($closing ? slideDown : slideUp)} 0.25s ease both;

  /* phone: leave clear breathing room above and below the modal */
  @media (max-width: 600px) {
    max-height: 84vh;
  }
`;

/* Scroll body (phone modal template, variant b — no bottom button): the head
   is a fixed non-scrolling zone above; only the plans scroll here. Always-visible
   scrollbar on the right with a reserved gutter; scroll doesn't chain to the page. */
export const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(235, 194, 142, 0.6) transparent;

  &::-webkit-scrollbar { width: 12px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(235, 194, 142, 0.55);
    border-radius: 6px;
    border: 4px solid transparent;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(235, 194, 142, 0.75);
    background-clip: padding-box;
  }
`;

/* Fixed (non-scrolling) header zone — ONLY "Choose Your Option" lives here,
   left-aligned with the close ✕ over its right. Everything else (cover, track
   name, composer) scrolls below. Height is symmetric around the ✕: it sits
   16px from the top and is 36px tall (bottom at 52px), so the header is
   52 + 16 = 68px tall — equal breathing room above and below the ✕ — with the
   heading centered on the ✕ line (34px). */
export const ModalHead = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  position: relative;
  /* desktop: the heading sits a bit lower (no need to centre on the ✕) and
     close to the content below */
  padding: 24px 48px 8px;

  @media (max-width: 860px) {
    padding: 24px 20px 8px;
  }
  /* phone: centre symmetrically on the ✕ */
  @media (max-width: 600px) {
    min-height: 68px;
    padding: 0 20px;
  }
`;

export const ModalContent = styled.div`
  /* tight top so the content sits close under the (lowered) heading */
  padding: 8px 48px 48px;

  @media (max-width: 860px) {
    padding: 8px 20px 32px;
  }
  /* phone: no top gap — the thumbnail starts right at the fixed header so no
     vertical space is wasted */
  @media (max-width: 600px) {
    padding-top: 0;
  }
`;

export const ModalHeading = styled.div`
  font-family: "Raleway", sans-serif;
  /* desktop size; phone steps down below */
  font-size: 1rem;
  font-weight: 400;
  /* Peach accent — same as colors.text.accentPeach (#ffdab9), matches the
     "QUESTION X OF Y" step counter in QuestionFlowDialog. */
  color: #ffdab9;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0;
  display: inline-flex;
  align-items: center;

  @media (max-width: 600px) {
    font-size: 0.8125rem;
  }
`;

/* ── Close button ── */

export const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(235, 194, 142, 0.2);
  border: 1px solid rgba(235, 194, 142, 0.3);
  /* Dim by default → fully white on hover. */
  color: rgba(255, 255, 255, 0.55);
  font-size: 18px;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(235, 194, 142, 0.35);
    border-color: rgba(235, 194, 142, 0.5);
    color: #ffffff;
  }
`;

/* ── Track header ── */

export const TrackHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(235, 194, 142, 0.15);
  text-align: left;
`;

export const CoverWrapper = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  cursor: pointer;
  border-radius: 12px;
  overflow: hidden;

`;

export const CoverPlayButton = styled.div<{ $isPlaying?: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  opacity: ${({ $isPlaying }) => ($isPlaying ? 1 : 0)};
  transition: opacity 0.2s ease;

  ${CoverWrapper}:hover & {
    opacity: 1;
  }
`;

export const TrackCover = styled.img`
  width: 72px;
  height: 72px;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
  transition: filter 0.2s ease;
`;

export const TrackCoverFallback = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 12px;
  background: linear-gradient(135deg, #3c2d23 0%, #5a4535 100%);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ebc28e;
  font-size: 1.5rem;
  transition: filter 0.2s ease;
`;

export const PlayBtn = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(0, 0, 0, 0.35) 0%, transparent 70%);
  border: none;
  padding: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  transition: opacity 0.2s ease;
  z-index: 2;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 36px;
    height: 36px;
    fill: currentColor;
    stroke: none;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
  }
`;

export const TrackInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;

  /* phone: tighten the gap between the track name and the artist name */
  @media (max-width: 600px) {
    gap: 0;
  }
`;

export const TrackName = styled.div`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.7rem;
  font-weight: 700;
  color: #ffffff;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0 8px;

  @media (max-width: 600px) {
    font-size: 1.3rem;
    line-height: 1.1;
  }
`;

export const ComposerName = styled.span`
  /* artist in Raleway — reads as a subtitle next to the Cormorant track name */
  font-family: "Raleway", sans-serif;
  font-size: 1rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);

  @media (max-width: 600px) {
    font-size: 0.9rem;
  }
`;

/* ── Mode toggle ── */

export const ToggleWrap = styled.div`
  display: flex;
  position: relative;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: 8px;
  padding: 3px;
  gap: 4px;
  width: fit-content;
`;

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 26px;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ToggleOption = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Raleway", sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  border-radius: 6px;
  padding: 0 16px;
  height: 36px;
  color: ${({ $active }) => ($active ? "#FBF4EC" : "rgba(251,244,236,0.45)")};
  background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.15)" : "transparent")};

  &:hover {
    color: #fbf4ec;
    background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.15)" : "rgba(255, 255, 255, 0.06)")};
  }

  @media (max-width: 600px) {
    padding: 0 16px;
    font-size: 0.8125rem;
    height: 32px;
  }
`;

export const HotPill = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 5px 8px;
  border-radius: 8px;
  background: #9cd3fd;
  color: #193e5b;
  text-transform: uppercase;
  margin-left: 6px;
  line-height: 1;
`;

export const BillingRow = styled.div`
  display: flex;
  justify-content: center;
  /* More breathing room between the "Subscription" title and the toggle. */
  padding: 16px 0 8px;
  /* in-card billing toggle is phone/tablet only; desktop shows it above the
     boxes (DesktopBillingRow) */
  @media (min-width: 861px) {
    display: none;
  }
`;

/* desktop only: billing toggle sits above the plan boxes, left-aligned with
   the left edge of the plan cards (not centered). The asymmetric margin keeps
   the toggle vertically CENTERED in the gap between the track-header divider
   (16px above, from TrackHeader) and the cards box (20px below, from PlansGrid's
   flex gap): 16+11 = 20+7 = 27px on each side. */
export const DesktopBillingRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin: 11px 0 7px;

  @media (max-width: 860px) {
    display: none;
  }
`;

export const BillingToggleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const BillingLabel = styled.button<{ $active?: boolean }>`
  font-family: "Raleway", sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  letter-spacing: 0.02em;
  color: #FBF4EC;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;
  padding: 0;

  &:hover {
    color: #fbf4ec;
  }
`;

export const BillingSwitch = styled.button<{ $annual?: boolean }>`
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: 1px solid ${({ $annual }) => ($annual ? "rgba(255, 255, 255, 0.45)" : "rgba(255, 255, 255, 0.15)")};
  background: ${({ $annual }) => ($annual ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.06)")};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  padding: 0;
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${({ $annual }) => ($annual ? "19px" : "2px")};
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${({ $annual }) => ($annual ? "#ffffff" : "rgba(255, 255, 255, 0.35)")};
    transition: left 0.2s ease, background 0.2s ease;
  }
`;

export const SaveBadge = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #34d399;
  background: rgba(52, 211, 153, 0.15);
  padding: 3px 8px;
  border-radius: 8px;
  letter-spacing: 0.3px;
`;

export const CardSaveBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  color: #3c2d23;
  /* "Popular" highlight pill — the site gold gradient (same as the /features
     pricing "Popular" badge) with dark text, so it matches the gold brand
     accent everywhere this modal opens. */
  background: linear-gradient(93deg, #ba905c 0.82%, #ffd29b 33.73%, #ffe0b1 47.65%, #ffd29b 61.53%, #ba905c 99.48%);
  /* 5/5 top/bottom (was 4/6) nudges the label down ~1px, optically centred. */
  padding: 5px 8px 5px;
  border-radius: 8px;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.35);
`;

export const InlinePill = styled.span<{ $variant: "free" | "premium" }>`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 7px 3px;
  border-radius: 8px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  vertical-align: middle;
  margin-right: 2px;
  position: relative;
  top: -1px;

  ${({ $variant }) =>
    $variant === "premium"
      ? `background: ${colors.tier.premium.bg}; color: ${colors.tier.premium.text};`
      : `background: ${colors.tier.free.bg}; color: ${colors.tier.free.text};`}
`;

export const TierPill = styled.span<{ $variant: "free" | "premium" }>`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 10px;
  border-radius: 10px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  display: inline-flex;
  align-items: center;
  margin-left: 10px;

  ${({ $variant }) =>
    $variant === "premium"
      ? `background: ${colors.tier.premium.bg}; color: ${colors.tier.premium.text};`
      : `background: ${colors.tier.free.bg}; color: ${colors.tier.free.text};`}

  /* phone + tablet: premium is shown as a diamond in front of the heading instead */
  @media (max-width: 860px) {
    ${({ $variant }) => $variant === "premium" && "display: none;"}
  }
`;

/* gold diamond shown AFTER the heading on phone + tablet (premium tracks) */
export const PremiumDiamond = styled.span`
  display: none;

  @media (max-width: 860px) {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    margin-left: 8px;

    svg {
      width: 12px;
      height: 12px;
      fill: #ffd29b;
    }
  }
`;

/* ── Subscription grid ── */

export const SubGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const SubFeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
  width: 100%;
  flex: 1;
`;

export const SubFeature = styled.li`
  font-family: "Raleway", sans-serif;
  font-size: 0.9rem;
  color: #f9f0eb;
  line-height: 1.6;
  text-align: left;
  padding-left: 18px;
  text-indent: -18px;
  padding-top: 3px;
  padding-bottom: 3px;
`;

export const PriceUnit = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  color: #9cd3fd;
  line-height: 1;
  /* Clear gap between the amount and the unit ("monthly" / "one-time"). */
  margin-top: 6px;
`;

/* Amount + unit as ONE centred, line-height:1 block. Wrapping them takes the
   big 2rem line-height out of the spacing maths: the gap ABOVE the amount =
   PlanPrice's padding-top; the gap BELOW the unit = this margin-bottom + the
   PlanPrice 6px flex row-gap. On the single-license card (padding-top:18) →
   below = 12 + 6 = 18 = above, so the price reads evenly spaced top & bottom. */
export const PriceLead = styled.div`
  flex: 0 0 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
  margin-bottom: 16px;
`;

export const AnnualTotal = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  color: #9cd3fd;
  background: rgba(156, 211, 253, 0.1);
  border: 1px solid rgba(156, 211, 253, 0.4);
  padding: 6px 14px;
  border-radius: 20px;
  text-align: center;
  width: auto;
  margin-bottom: 0;
`;

export const SubTagline = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 0.8rem;
  color: rgba(251, 244, 236, 0.5);
  text-align: center;
  font-style: italic;
  margin-bottom: 16px;
`;

/* ── Plans grid ── */

export const PlansGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const PlansRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const OtherUseBox = styled.div`
  border: 1px solid rgba(235, 194, 142, 0.15);
  border-radius: 16px;
  background: rgba(81, 58, 40, 0.3);
  padding: 14px 24px;
  align-items: center;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    border-color: rgba(235, 194, 142, 0.4);
    background: rgba(81, 58, 40, 0.5);
  }

  @media (max-width: 860px) {
    flex-direction: column;
    text-align: center;
    padding: 20px;

    /* the "Send Message" button spans the full card width when stacked */
    button {
      width: 100%;
    }
  }
`;

export const OtherUseContent = styled.div`
  flex: 1;
`;

export const OtherUseTitle = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #ffffff;
  margin-bottom: 0;
`;

export const OtherUseDesc = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 0.8125rem;
  color: rgba(251, 244, 236, 0.5);
  line-height: 1.5;
`;

/* ── Single plan column ── */

export const PlanColumn = styled.div<{ $added?: boolean; $highlighted?: boolean }>`
  border: 1px solid rgba(235, 194, 142, 0.3);
  border-radius: 16px;
  padding: 24px;
  /* Same treatment as the /pricing boxes: one flat ground with a soft round
     light from above, painted as a background LAYER so it always sits behind
     the content and is clipped by the card's own radius. */
  background:
    radial-gradient(125% 62% at 50% -8%, rgba(255, 226, 185, 0.20) 0%, rgba(255, 226, 185, 0.08) 36%, rgba(255, 226, 185, 0) 72%),
    rgba(113, 81, 55, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;
  transition: background 0.25s ease;

  &:hover {
    background: rgba(235, 194, 142, 0.08);
  }

  &::after {
    content: "";
    position: absolute;
    inset: -20px;
    width: 90%;
    background: linear-gradient(
      105deg,
      transparent 0%,
      rgba(255, 220, 168, 0.1) 20%,
      rgba(255, 210, 155, 0.3) 45%,
      rgba(255, 220, 168, 0.3) 55%,
      rgba(255, 210, 155, 0.1) 80%,
      transparent 100%
    );
    filter: blur(15px);
    pointer-events: none;
    opacity: 0;
  }

  ${({ $highlighted }) =>
    $highlighted &&
    css`
      background: linear-gradient(
        180deg,
        rgba(186, 144, 92, 0.2) 0%,
        rgba(113, 81, 55, 0.2) 60%,
        rgba(113, 81, 55, 0.2) 100%
      );
      border-color: #ffd29b;
      box-shadow: 0 8px 32px rgba(186, 144, 92, 0.4);
    `}

  ${({ $added }) =>
    $added &&
    css`
      background: linear-gradient(
        135deg,
        rgba(186, 144, 92, 0.2) 0%,
        rgba(255, 210, 155, 0.2) 100%
      );
      border-color: #ffd29b;
      box-shadow: 0 8px 32px rgba(186, 144, 92, 0.4);

      &::after {
        opacity: 1;
        animation: ${cardSweep} 1.2s ease-in-out forwards;
      }
    `}
`;

export const PlanIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px solid rgba(235, 194, 142, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  color: #ebc28e;
  font-size: 1.4rem;
`;

export const PlanTitle = styled.div`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.65rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: -4px;
  text-align: center;
  line-height: 1.15;

  /* Two-column layout (>860px) makes each card narrow — trim the title so the
     longer "YouTube Video License" stays on one line, aligned with the
     "Subscription" card. Single-column (≤860, full-width cards) keeps 1.5rem. */
  @media (min-width: 861px) {
    font-size: 1.25rem;
  }
`;

export const PlanPrice = styled.div<{ $single?: boolean }>`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: #9cd3fd;
  text-align: center;
  /* Phone/tablet: cards STACK, so this is per-card in-card balance (the
     Subscription card carries the in-card billing toggle, the Single card
     doesn't — hence the larger top padding on Single). */
  padding-top: ${({ $single }) => ($single ? "22px" : "12px")};
  padding-bottom: ${({ $single }) => ($single ? "16px" : "10px")};
  margin-bottom: 10px;
  width: 100%;
  min-height: 110px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;

  /* Desktop: the two cards sit side by side, so BOTH use the SAME top/bottom
     spacing — keeps the price block (and everything below it) aligned across
     the two cards. */
  @media (min-width: 861px) {
    padding-top: 16px;
    padding-bottom: 12px;
  }
`;

export const StrikethroughPrice = styled.span`
  text-decoration: line-through;
  opacity: 0.4;
  font-weight: 400;
`;

export const FreePrice = styled.span`
  color: #34d399;
  font-size: 2rem;
  font-weight: 700;
`;

export const PlanDescContainer = styled.div`
  flex: 1;
  margin-bottom: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const PlanDesc = styled.div<{ $warning?: boolean }>`
  color: #f9f0eb;
  font-family: "Raleway", sans-serif;
  font-size: 0.9rem;
  text-align: left;
  line-height: 2;
  padding-left: 8px;

  ${({ $warning }) =>
    $warning &&
    css`
      margin-top: 0;
      margin-bottom: 8px;
    `}
`;

export const CheckIcon = styled.span`
  /* Green, matching the pricing page's Check (colors.accent.green). It was gold,
     which is this site's "press me" colour — a tick is a statement of fact, not
     something to click, and the two surfaces disagreed. */
  color: #34d399;
  margin-right: 6px;
  font-size: 0.85rem;
`;

export const WarningIcon = styled.span`
  color: #ff6b6b;
  margin-right: 5px;
  font-size: 1rem;
`;

export const LicenseLink = styled.button`
  display: inline-block;
  background: none;
  border: none;
  color: #ffd29b;
  font-family: "Raleway", sans-serif;
  font-size: 0.85rem;
  text-align: center;
  line-height: 1;
  padding: 10px 16px;
  border-radius: 20px;
  margin-top: 12px;
  margin-bottom: 8px;
  align-self: center;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: rgba(255, 210, 155, 0.15);
    color: #fff;
  }
`;

/* ── Buttons ── */

const addedToCart = keyframes`
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.03); }
`;

export const PlanButton = styled.button<{ $added?: boolean; $gold?: boolean }>`
  width: 100%;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-family: "Raleway", sans-serif;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease,
    color 0.3s ease, transform 0.2s ease;

  /* blue icon, like the golden buttons on the home page */
  svg {
    width: 18px;
    height: 18px;
    color: #60a3d7;
  }

  ${({ $added }) =>
    $added
      ? css`
          background: transparent;
          border: 1.5px solid rgba(186, 144, 92, 0.5);
          color: #f3d0a4;
          cursor: default;
          animation: ${addedToCart} 0.4s ease;
        `
      : css`
          /* Golden button — same look as the home page primary buttons:
             lighter 135deg gold gradient, no border, warm glow. */
          background: linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%);
          border: none;
          color: #3c2d23;
          box-shadow: 0 0 32px rgba(186, 144, 92, 0.4);

          &:hover {
            box-shadow: 0 0 40px rgba(186, 144, 92, 0.6);
            transform: translateY(-2px);
          }

          &:hover::before {
            animation: ${btnShine} 0.5s ease-out;
          }
        `}

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    pointer-events: none;
  }

  ${({ $gold }) =>
    $gold &&
    css`
      background: linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%);
      border: none;
      color: #3c2d23;
      box-shadow: 0 0 32px rgba(186, 144, 92, 0.4);

      &:hover {
        box-shadow: 0 0 40px rgba(186, 144, 92, 0.6);
        transform: translateY(-2px);
      }
    `}
`;

/* ── License Terms Modal ──
   The modal itself reuses the shared phone-template pieces (Overlay /
   ModalContainer / ModalHead / ScrollArea / ModalContent / CloseButton); only
   the per-section typography below is bespoke. */

export const TermsSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  h3 {
    font-family: "Cormorant Garamond", serif;
    font-size: 1.1rem;
    font-weight: 700;
    color: #fff;
    margin: 0 0 12px;
  }

  p {
    font-family: "Raleway", sans-serif;
    font-size: 0.95rem;
    color: #fff;
    line-height: 1.6;
    margin: 0 0 12px;
  }

  ul {
    list-style: none;
    padding-left: 0;
    margin: 0;
  }

  li {
    font-family: "Raleway", sans-serif;
    font-size: 0.95rem;
    color: #fff;
    line-height: 1.6;
    margin-bottom: 8px;
    padding-left: 24px;
    position: relative;

    &::before {
      content: "\u2022";
      position: absolute;
      left: 8px;
      color: #ba905c;
      font-weight: bold;
    }
  }
`;

/* ── Whitelisting Stepper ── */

export const WhitelistingStepper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(235, 194, 142, 0.15);
  border-radius: 20px;
  padding: 6px;
  justify-content: space-between;
  /* Match the height of the sibling stepper pill (whose +/- buttons set its
     height): the text-only "Any YouTube Channel" / "Use in any video" pills had
     no buttons, so they rendered shorter. box-sizing so min-height includes the
     padding + border. WhitelistingBtn is 22px (desktop) / 34px (phone). */
  box-sizing: border-box;
  min-height: 36px;

  @media (max-width: 600px) {
    min-height: 48px;
  }
`;

/* Matches the StepperBtn in PlanConfigView — solid blue disc, dark brown
   glyph, lighter blue + darker glyph on hover, no border, no movement. */
export const WhitelistingBtn = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgb(156, 211, 253);
  color: rgb(97, 84, 73);
  font-size: 0.875rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  padding: 0;
  flex-shrink: 0;

  /* Hover only on real pointers — iOS leaves :hover stuck after a tap, so the
     button keeps the hover bg/color until you tap elsewhere. */
  @media (hover: hover) {
    &:hover {
      background: #c7e4fb;
      color: #3c2d23;
    }
  }

  /* phone: bigger tap target so fingers can press +/- reliably */
  @media (max-width: 600px) {
    width: 34px;
    height: 34px;
    font-size: 1.25rem;
    /* The +/− glyphs sit on the font's math axis, ~1px BELOW the circle's
       geometric centre. box-sizing:border-box + a 2px bottom padding shrinks the
       content box from the bottom, nudging the glyph up ~1px to optical centre
       (same correction as the /features PricingPlans stepper buttons). */
    box-sizing: border-box;
    padding: 0 0 2px 0;
  }
`;

export const WhitelistingText = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  color: #fbf4ec;
  white-space: nowrap;
`;

export const WhitelistingCount = styled.span`
`;

/* Invisible, desktop-only placeholder that mirrors the Single card's "View full
   license terms" link (same margin-top + font-size + block width), so the two
   plan buttons stay on the same horizontal line. ≤860px the cards stack, so
   there's no alignment to preserve — hidden there. */
export const PlanButtonLinkSpacer = styled.span`
  display: block;
  width: 100%;
  margin-top: 12px;
  font-size: 0.8125rem;
  text-align: center;
  visibility: hidden;
  pointer-events: none;
  user-select: none;

  @media (max-width: 860px) {
    display: none;
  }
`;

export const PlansMainRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
  align-items: stretch;

  /* tablet & phone: stack the two plan cards so they aren't cramped */
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

export const SubscriptionArea = styled.div`
  flex: 2 1 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

export const SubscriptionCards = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  flex: 1;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

export const SingleArea = styled.div`
  flex: 0.95 1 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 60px;

  ${PlanColumn} {
    flex: 1;
    justify-content: flex-start;
  }
`;

export const AreaLabel = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: rgba(251, 244, 236, 0.35);
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

